import { Grid } from '../common/Grid';
import React, { useState, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, CircularProgress, Alert, Chip, IconButton, Collapse,
  Card, CardContent, Divider, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  RecordVoiceOver as CounselIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  EditCalendar as EditCalendarIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import counselingService from '../../services/counselingService';
import { getUserDisplayName, useBatchUserLookup } from '../../hooks/useUserLookup';
import { getCounselingStatusDisplay, getDeclinedReasonLabel, isAcknowledgmentCounselingRecord } from '../../constants/counselingConstants';
import { ISEC_QUESTIONS, ISEC_POINT_IDS, ISEC_LETTER_COLORS } from '../../constants/isecQuestions';
import { toIsecPoints } from '../counseling/counselingChecklist';
import CounselingDialog from '../counseling/CounselingDialog';
import { openReportWindow, renderRxReport, fetchReportHardcopyImages } from './rxReportPrint';
import { fetchRxTimelineEntries } from './rxTimelineData';
import { headerStripSurface } from '../../theme/gridSurfaces';

interface CounselingHistoryItem {
  id: string;
  rxNumber?: string;
  drugName?: string;
  din?: string;
  doctorName?: string;
  status: string;
  reason: string;
  counselingBy: string;
  method: string;
  checklist: Record<string, { checked: boolean; notes: string }>;
  comments?: string;
  declinedReason?: string;
  createdBy?: string;
  completedBy?: string;
  createdAt: string;
  completedAt?: string;
  /** GP-1203: set by the backend when a LATER counseling for the same drug under
   *  a different Rx completed this one — the cancelled-and-rebuilt-Rx case. */
  supersededByRxNumber?: string;
  // Calendar date the session was counselled (editable). When set it is the
  // authoritative "Date" shown; otherwise we fall back to createdAt. GP-1164.
  dateCounselled?: string;
}

const METHOD_LABELS: Record<string, string> = {
  by_phone: 'By Phone',
  in_person: 'In Person',
  video: 'Video',
  written: 'Written',
};

const BY_LABELS: Record<string, string> = {
  patient: 'Patient',
  caregiver: 'Caregiver',
  representative: 'Representative',
};

interface CounselingHistoryTabProps {
  patientId: string;
  pharmacyId?: string;
  patientName?: string;
}

const CounselingHistoryTab: React.FC<CounselingHistoryTabProps> = ({ patientId, pharmacyId, patientName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<CounselingHistoryItem[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  // Manual counseling entry from the patient record (no order context). The
  // dialog supports a patient-level create via counselingOrigin='task_queue'
  // (same path the Task Queue uses when a task has no order) — GP-1117.
  const [addOpen, setAddOpen] = useState(false);

  // pharmacyId is passed from Patient 360, but fall back to the composite id's
  // prefix (`<pharmacyId>_<krollId>`) so the create button works even if the
  // active-pharmacy prop is momentarily empty.
  const resolvedPharmacyId = pharmacyId || patientId.split('_')[0];

  // Resolve the signing pharmacist (completedBy) to a display name. Batch lookup
  // re-renders when names load; getUserDisplayName covers plain-email / non-Auth0 ids.
  const { userNames } = useBatchUserLookup(records.map((r) => r.completedBy));
  const resolveUserName = (id?: string): string => {
    if (!id) return '—';
    return userNames.get(id) || getUserDisplayName(id);
  };

  // GP-1164: delete a redundant/duplicate entry, and edit an entry's date.
  const [deleteTarget, setDeleteTarget] = useState<CounselingHistoryItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editDateTarget, setEditDateTarget] = useState<CounselingHistoryItem | null>(null);
  const [editDateValue, setEditDateValue] = useState('');
  const [savingDate, setSavingDate] = useState(false);

  // The date shown in the "Date" column: the pharmacist-set counselling date when
  // present, otherwise the record's createdAt. `dateCounselled` comes back from a
  // `date` column as a plain YYYY-MM-DD — parse it at local midnight (never via
  // UTC, per the date-handling rules) so the day never shifts.
  const displayDate = (item: CounselingHistoryItem): Date =>
    item.dateCounselled
      ? parseISO(`${item.dateCounselled.split('T')[0]}T00:00:00`)
      : new Date(item.createdAt);

  useEffect(() => {
    fetchHistory();
  }, [patientId]);

  const openEditDate = (item: CounselingHistoryItem) => {
    setEditDateValue(format(displayDate(item), 'yyyy-MM-dd'));
    setEditDateTarget(item);
  };

  const handleSaveDate = async () => {
    if (!editDateTarget || !editDateValue) return;
    setSavingDate(true);
    try {
      await counselingService.updateRecord(resolvedPharmacyId, editDateTarget.id, {
        dateCounselled: editDateValue,
      });
      enqueueSnackbar('Counseling date updated', { variant: 'success' });
      setEditDateTarget(null);
      fetchHistory();
    } catch (error) {
      console.error('Error updating counseling date:', error);
      enqueueSnackbar('Failed to update counseling date', { variant: 'error' });
    } finally {
      setSavingDate(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await counselingService.deleteRecord(resolvedPharmacyId, deleteTarget.id);
      enqueueSnackbar('Counseling entry deleted', { variant: 'success' });
      setDeleteTarget(null);
      fetchHistory();
    } catch (error) {
      console.error('Error deleting counseling entry:', error);
      enqueueSnackbar('Failed to delete counseling entry', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      // GP-1155: safety/interaction acknowledgments are persisted as counseling
      // records (no Rx/drug, empty 0/13 checklist) but are NOT counseling sessions
      // — drop them so they don't surface as a blank "Completed" row. Their full
      // text lives in the auto-created clinical note (patient file → All Notes).
      const all = (await counselingService.getRecordsByPatient(patientId)) as unknown as CounselingHistoryItem[];
      setRecords(all.filter((r) => !isAcknowledgmentCounselingRecord(r)));
    } catch (error: any) {
      console.error('Error fetching counseling history:', error);
      enqueueSnackbar('Failed to load counseling history', { variant: 'error' });
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandRow = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // GP-1177: turn a counseling session into the FULL consolidated per-Rx report —
  // the "beautiful counseling note" the RX View had no way to print. Rather than
  // print the counseling row alone (blank verification / "No notes"), we merge the
  // same per-Rx timeline data the RX History tab shows (verification + every
  // counseling record + Rx notes) and embed the hardcopy images, so the document
  // is complete no matter which tab it was printed from. Falls back to the single
  // record only when the Rx has no timeline entry. The window is opened
  // synchronously (preserving the click gesture) before the async fetches.
  const handlePrintCounseling = async (item: CounselingHistoryItem) => {
    const win = openReportWindow();
    if (!win) {
      enqueueSnackbar('Please allow pop-ups to generate the report.', { variant: 'warning' });
      return;
    }
    const resolveUser = (id?: string | null) => resolveUserName(id ?? undefined);
    try {
      let entry = null as Awaited<ReturnType<typeof fetchRxTimelineEntries>>[number] | null;
      if (item.rxNumber) {
        const entries = await fetchRxTimelineEntries(patientId).catch(() => []);
        entry = entries.find((e) => e.rxNumber === item.rxNumber) || null;
      }
      const reportEntry = entry ?? {
        rxNumber: item.rxNumber || '—',
        medicationName: item.drugName || 'Unknown',
        orderId: '',
        counselingRecords: [item as any],
        notes: [],
      };
      const { images, unavailable } = await fetchReportHardcopyImages(
        resolvedPharmacyId,
        patientId,
        reportEntry.rxNumber,
      );
      renderRxReport(win, reportEntry, {
        patientName,
        resolveUser,
        hardcopyImages: images,
        hardcopyUnavailable: unavailable,
      });
    } catch {
      win.close();
      enqueueSnackbar('Failed to generate the report.', { variant: 'error' });
    }
  };

  const renderExpandedContent = (item: CounselingHistoryItem) => {
    // Render from the SHARED ISEC checklist definition (constants/isecQuestions),
    // the same source of truth Clinical Verification and the Counseling queue use,
    // so the item list, Indication/Effectiveness/Safety/Compliance grouping, and
    // completion count are identical everywhere (GP-1162). Legacy 13-item records
    // are normalized into the v2 per-point shape so no documented point is lost.
    const points = toIsecPoints(item.checklist);
    const checkedCount = ISEC_POINT_IDS.filter((id) => points[id]?.checked).length;

    return (
      <Box sx={{ p: 2, bgcolor: headerStripSurface }}>
        <Grid container spacing={2}>
          {/* Checklist */}
          <Grid item xs={12} md={8}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <CounselIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Counseling Checklist ({checkedCount}/{ISEC_POINT_IDS.length} items covered)
                  </Typography>
                  {/* GP-1177: print / share this counseling session as one document */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handlePrintCounseling(item)}
                    sx={{ textTransform: 'none', flexShrink: 0 }}
                  >
                    Print Report
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {ISEC_QUESTIONS.map((group) => (
                    <Box key={group.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '4px',
                            bgcolor: ISEC_LETTER_COLORS[group.key],
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                          }}
                        >
                          {group.letter}
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {group.title}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 0.5 }}>
                        {group.questions.map((q) => {
                          const value = points[q.id] || { checked: false, notes: '' };
                          return (
                            <Box key={q.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {value.checked
                                  ? <CheckCircleIcon color="success" fontSize="small" />
                                  : <UncheckedIcon sx={{ color: 'text.disabled' }} fontSize="small" />
                                }
                                <Typography
                                  variant="body2"
                                  color={value.checked ? 'text.primary' : 'text.secondary'}
                                >
                                  {q.text}
                                </Typography>
                              </Box>
                              {value.notes && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 4, display: 'block', fontStyle: 'italic' }}
                                >
                                  {value.notes}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Session Details + Comments */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Session Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Method:</strong> {METHOD_LABELS[item.method] || item.method}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Counseled to:</strong> {BY_LABELS[item.counselingBy] || item.counselingBy}
                    </Typography>
                    {item.doctorName && (
                      <Typography variant="body2">
                        <strong>Prescriber:</strong> {item.doctorName}
                      </Typography>
                    )}
                    {item.completedBy && (
                      <Typography variant="body2">
                        <strong>Counseled by:</strong> {resolveUserName(item.completedBy)}
                      </Typography>
                    )}
                    {item.completedAt && (
                      <Typography variant="body2">
                        <strong>Completed:</strong>{' '}
                        {format(new Date(item.completedAt), 'MMM d, yyyy HH:mm')}
                      </Typography>
                    )}
                    {item.declinedReason && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                        <Typography variant="body2">
                          <strong>Declined Reason:</strong> {getDeclinedReasonLabel(item.declinedReason)}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Button
                    size="small"
                    startIcon={<EditCalendarIcon />}
                    onClick={() => openEditDate(item)}
                  >
                    Edit date
                  </Button>
                </CardContent>
              </Card>

              {item.comments && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Comments
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {item.comments}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Created by: ${getUserDisplayName(item.createdBy) || 'Unknown'}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Created: ${format(new Date(item.createdAt), 'MMM d, yyyy HH:mm')}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Shared "Add Counseling" action — surfaced in both the empty state and the
  // populated header so a pharmacist can record a session directly from the
  // patient record, not only from Clinical Verification / the Task Queue (GP-1117).
  const addCounselingButton = (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddIcon />}
      onClick={() => setAddOpen(true)}
    >
      Add Counseling
    </Button>
  );

  // The create dialog opens with only patient context (no order/Rx). It persists
  // via POST /api/counseling with counselingOrigin='task_queue', then we refetch.
  const addCounselingDialog = addOpen && (
    <CounselingDialog
      open={addOpen}
      onClose={() => setAddOpen(false)}
      orderData={{
        pharmacyId: resolvedPharmacyId,
        patientId,
        patientName,
      }}
      counselingOrigin="task_queue"
      onComplete={() => {
        setAddOpen(false);
        fetchHistory();
      }}
    />
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (records.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6">Counseling History</Typography>
          {addCounselingButton}
        </Box>
        <Alert severity="info">
          <Typography variant="body2">
            No counseling records found for this patient.
          </Typography>
        </Alert>
        {addCounselingDialog}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Counseling History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patient counseling sessions recorded during clinical verification
          </Typography>
        </Box>
        {addCounselingButton}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Date</TableCell>
              <TableCell>Rx #</TableCell>
              <TableCell>Drug</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Counseled To</TableCell>
              <TableCell>Counseled By</TableCell>
              <TableCell>Checklist</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((item) => {
              // Count against the shared ISEC checklist (normalizing legacy 13-item
              // records) so the x/N here matches Clinical Verification's total (GP-1162).
              const points = toIsecPoints(item.checklist);
              const checkedCount = ISEC_POINT_IDS.filter((id) => points[id]?.checked).length;
              const totalCount = ISEC_POINT_IDS.length;
              return (
                <React.Fragment key={item.id}>
                  <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleExpandRow(item.id)}>
                        {expandedRow === item.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {format(displayDate(item), 'MMM d, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.rxNumber || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.drugName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {METHOD_LABELS[item.method] || item.method}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {BY_LABELS[item.counselingBy] || item.counselingBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {resolveUserName(item.completedBy)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {totalCount > 0 ? (
                        <Chip
                          label={`${checkedCount}/${totalCount}`}
                          size="small"
                          color={checkedCount === totalCount ? 'success' : checkedCount > 0 ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // GP-1203: a pending row whose Rx was cancelled and rebuilt
                        // will never be actioned — say so here rather than making
                        // staff cross-reference Prescriptions and Fill History.
                        if (item.supersededByRxNumber) {
                          return (
                            <Tooltip
                              title={`Counseling was completed on Rx #${item.supersededByRxNumber}, which replaced this prescription.`}
                            >
                              <Chip label="Superseded" size="small" color="default" variant="outlined" />
                            </Tooltip>
                          );
                        }
                        const s = getCounselingStatusDisplay(item.status, item.declinedReason);
                        return <Chip label={s.label} size="small" color={s.color} />;
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete entry">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Delete counseling entry"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                      <Collapse in={expandedRow === item.id} timeout="auto" unmountOnExit>
                        {renderExpandedContent(item)}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* GP-1164: confirm before removing a redundant/duplicate entry. */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete counseling entry?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the counseling entry
            {deleteTarget?.rxNumber ? ` for Rx ${deleteTarget.rxNumber}` : ''}
            {deleteTarget?.drugName ? ` (${deleteTarget.drugName})` : ''} dated{' '}
            {deleteTarget ? format(displayDate(deleteTarget), 'MMM d, yyyy') : ''}. Use this to
            clear an accidental duplicate — the entry is removed from this history and the action
            is recorded in the patient audit log.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GP-1164: edit an entry's counselling date. */}
      <Dialog open={!!editDateTarget} onClose={() => !savingDate && setEditDateTarget(null)}>
        <DialogTitle>Edit counseling date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label="Counseling date"
            value={editDateValue}
            onChange={(e) => setEditDateValue(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Counseling date' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDateTarget(null)} disabled={savingDate}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveDate} disabled={savingDate || !editDateValue}>
            {savingDate ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {addCounselingDialog}
    </Box>
  );
};

export default CounselingHistoryTab;
